import {
  PROTOCOL_VERSION,
  ServerToViewerSchema,
  type BrowserRuntime,
  type ErrorInfo,
  type InputEvent,
  type ParticipantKind,
  type ServerToViewer,
  type StreamMode,
  type ViewerProfileSnapshot,
  type ViewerToServer,
  type WindowSize,
} from '@br/protocol';
import { backoffDelay, nextRequestId, wsUrl } from '../lib/ws';
import type { FrameStore } from './frames';

type Joined = Extract<ServerToViewer, { t: 'joined' }>;
export type ViewerCaps = Joined['can'];
type Reply = Extract<ServerToViewer, { t: 'reply' }>;
type Request = Extract<ViewerToServer, { id: string }>;
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
export type ViewerRequest = DistributiveOmit<Request, 'id'>;

export type ViewerConnection = 'connecting' | 'joined' | 'reconnecting' | 'closed';

export interface StreamStatus {
  state: 'active' | 'paused' | 'stopped';
  reason?: string;
}

export interface ControlRequest {
  participantId: string;
  name: string;
  kind: ParticipantKind;
}

export interface ChooserRequest {
  tabId: string;
  chooserId: string;
  multiple: boolean;
}

export interface ViewerState {
  cursors?: Record<string, string>;
  connection: ViewerConnection;
  /** Set when `connection === 'closed'` and we will not reconnect */
  closed?: ErrorInfo;
  participantId?: string;
  kind?: ParticipantKind;
  can: ViewerCaps;
  snapshot?: ViewerProfileSnapshot;
  runtimeError?: ErrorInfo;
  /** Tab this participant is looking at */
  tabId?: string;
  mode: StreamMode;
  streams: Record<string, StreamStatus>;
  /** Incoming control request (only the controller receives these) */
  request?: ControlRequest;
  chooser?: ChooserRequest;
  progress?: { chooserId: string; receivedBytes: number };
}

export type ViewerEvent =
  | { type: 'notice'; level: 'info' | 'warn' | 'error'; message: string }
  | { type: 'control.changed'; controllerId?: string; reason: string; isMe: boolean }
  | { type: 'control.denied' }
  | { type: 'navigation_blocked'; tabId: string; url: string }
  | { type: 'kicked'; reason: string }
  | { type: 'joined' };

export class RequestError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'RequestError';
  }
}

export interface ViewerSocketOptions {
  profileId: string;
  supportSessionId?: string;
  tabId?: string;
  mode?: StreamMode;
  frames: FrameStore;
  onEvent?: (e: ViewerEvent) => void;
}

const NO_CAPS: ViewerCaps = {
  navigate: false,
  createTab: false,
  closeTab: false,
  resize: false,
  openBrowser: false,
  forceControl: false,
};
const PING_MS = 20_000;
const REQUEST_TIMEOUT_MS = 30_000;
/** Close codes after which reconnecting is pointless */
const FATAL_CLOSE = new Set([4401, 4403, 4404, 4426]);

interface Pending {
  resolve: (data: unknown) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * One `/ws/viewer` connection for one browser profile. Holds the live profile state for React
 * (`subscribe`/`getState` for `useSyncExternalStore`), delivers frames to a `FrameStore`, and
 * exposes request/reply helpers. Reconnects automatically unless the server told us to go away.
 */
export class ViewerSocket {
  private ws?: WebSocket;
  private state: ViewerState;
  private readonly listeners = new Set<() => void>();
  private readonly pending = new Map<string, Pending>();
  private pingTimer?: ReturnType<typeof setInterval>;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private attempt = 0;
  private stopped = true;
  private preferredTabId?: string;

  constructor(private readonly opts: ViewerSocketOptions) {
    this.preferredTabId = opts.tabId;
    this.state = {
      connection: 'connecting',
      can: NO_CAPS,
      mode: opts.mode ?? 'screencast',
      streams: {},
    };
  }

  get profileId(): string {
    return this.opts.profileId;
  }

  dismissClosedDialog(dialogId: string): void {
    this.patchSnapshot({
      dialogs: (this.state.snapshot?.dialogs ?? []).filter((d) => d.dialogId !== dialogId),
    });
  }

  // ---------------------------------------------------------------- store api

  getState = (): ViewerState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private setState(patch: Partial<ViewerState>) {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  private patchSnapshot(patch: Partial<ViewerProfileSnapshot>) {
    if (!this.state.snapshot) return;
    this.setState({ snapshot: { ...this.state.snapshot, ...patch } });
  }

  // ---------------------------------------------------------------- lifecycle

  connect(): void {
    this.stopped = false;
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;
    this.open();
  }

  /** Closes for good (navigating away). */
  close(): void {
    this.stopped = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = undefined;
    this.stopPing();
    this.rejectAll(new RequestError('closed', '연결이 종료되었습니다'));
    const ws = this.ws;
    this.ws = undefined;
    if (ws && ws.readyState <= WebSocket.OPEN) {
      try {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ t: 'leave' }));
      } catch {
        /* ignore */
      }
      ws.close(1000, 'left');
    }
    this.setState({ connection: 'closed', closed: undefined });
  }

  private open() {
    const ws = new WebSocket(wsUrl('/ws/viewer'));
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    this.setState({
      connection: this.attempt === 0 ? 'connecting' : 'reconnecting',
      closed: undefined,
    });

    ws.onopen = () => {
      if (this.ws !== ws) return;
      void this.join();
    };
    ws.onmessage = (ev) => {
      if (this.ws !== ws) return;
      if (typeof ev.data === 'string') this.onText(ev.data);
      else if (ev.data instanceof ArrayBuffer) void this.opts.frames.push(new Uint8Array(ev.data));
    };
    ws.onclose = (ev) => {
      if (this.ws !== ws) return;
      this.ws = undefined;
      this.stopPing();
      this.rejectAll(new RequestError('disconnected', '연결이 끊어졌습니다'));
      if (this.stopped) return;
      if (this.state.connection === 'closed') return; // kicked
      if (FATAL_CLOSE.has(ev.code)) {
        this.setState({
          connection: 'closed',
          closed: { code: String(ev.code), message: ev.reason || '연결이 거부되었습니다' },
        });
        return;
      }
      const delay = backoffDelay(this.attempt++);
      this.setState({ connection: 'reconnecting', streams: {} });
      this.reconnectTimer = setTimeout(() => this.open(), delay);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
  }

  private async join() {
    const tabId = this.state.tabId ?? this.preferredTabId;
    try {
      await this.request({
        t: 'join',
        protocolVersion: PROTOCOL_VERSION,
        profileId: this.opts.profileId,
        supportSessionId: this.opts.supportSessionId,
        tabId,
        stream: { mode: this.state.mode, params: {} },
      });
      this.attempt = 0;
      this.startPing();
    } catch (err) {
      const e = err instanceof RequestError ? err : new RequestError('join_failed', String(err));
      // The server closes the socket right after a failed join; make sure we stop retrying.
      this.stopped = true;
      this.setState({ connection: 'closed', closed: { code: e.code, message: e.message } });
    }
  }

  private startPing() {
    this.stopPing();
    this.pingTimer = setInterval(() => this.send({ t: 'ping', ts: Date.now() }), PING_MS);
  }

  private stopPing() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = undefined;
  }

  private rejectAll(err: Error) {
    for (const p of this.pending.values()) {
      clearTimeout(p.timer);
      p.reject(err);
    }
    this.pending.clear();
  }

  // ---------------------------------------------------------------- sending

  get bufferedAmount(): number {
    return this.ws?.bufferedAmount ?? 0;
  }

  send(msg: ViewerToServer): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  sendBinary(buf: Uint8Array): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(buf);
    return true;
  }

  sendInput(event: InputEvent): void {
    this.send({ t: 'input', event });
  }

  request<T = unknown>(msg: ViewerRequest): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (this.ws?.readyState !== WebSocket.OPEN) {
        reject(new RequestError('disconnected', '서버와 연결되어 있지 않습니다'));
        return;
      }
      const id = nextRequestId();
      const timer = setTimeout(
        () => {
          this.pending.delete(id);
          reject(new RequestError('timeout', '서버 응답이 없습니다'));
        },
        msg.t === 'tab.navigate' || msg.t === 'tab.create' || msg.t === 'tab.reload'
          ? 45_000
          : REQUEST_TIMEOUT_MS,
      );
      this.pending.set(id, { resolve: (d) => resolve(d as T), reject, timer });
      this.ws.send(JSON.stringify({ ...msg, id } as ViewerToServer));
    });
  }

  /** Switches the viewed tab (and, for the controller, the remote active tab). */
  async selectTab(tabId: string): Promise<void> {
    if (this.state.tabId === tabId) {
      // Already viewing it. Re-selecting while it is paused asks the node to bring it to the
      // front again (the overlay promises exactly that); while active there is nothing to do.
      const stream = this.state.streams[tabId];
      if (stream && stream.state !== 'active') await this.request({ t: 'tab.select', tabId });
      return;
    }
    this.setState({ tabId });
    this.preferredTabId = tabId;
    try {
      await this.request({ t: 'tab.select', tabId });
    } catch (err) {
      if (this.state.tabId === tabId) this.setState({ tabId: undefined });
      throw err;
    }
  }

  clearRequest(): void {
    if (this.state.request) this.setState({ request: undefined });
  }

  clearChooser(): void {
    if (this.state.chooser || this.state.progress)
      this.setState({ chooser: undefined, progress: undefined });
  }

  // ---------------------------------------------------------------- receiving

  private onText(text: string) {
    let json: unknown;
    try {
      json = JSON.parse(text);
    } catch {
      return;
    }
    const parsed = ServerToViewerSchema.safeParse(json);
    if (!parsed.success) return;
    this.handle(parsed.data);
  }

  private emit(e: ViewerEvent) {
    this.opts.onEvent?.(e);
  }

  private handle(msg: ServerToViewer) {
    switch (msg.t) {
      case 'reply':
        return this.onReply(msg);
      case 'joined': {
        this.setState({
          connection: 'joined',
          closed: undefined,
          participantId: msg.participantId,
          kind: msg.kind,
          can: msg.can,
          snapshot: msg.snapshot,
          mode: msg.mode,
          tabId: msg.tabId,
          streams: {},
          request: undefined,
          chooser: undefined,
          progress: undefined,
        });
        this.emit({ type: 'joined' });
        return;
      }
      case 'snapshot':
        this.setState({ snapshot: msg.snapshot });
        return;
      case 'cursor.changed':
        this.setState({ cursors: { ...this.state.cursors, [msg.tabId]: msg.cursor } });
        return;
      case 'dialog.opened':
        this.patchSnapshot({
          dialogs: [
            ...(this.state.snapshot?.dialogs ?? []).filter((d) => d.tabId !== msg.dialog.tabId),
            msg.dialog,
          ],
        });
        return;
      case 'dialog.closed':
        this.dismissClosedDialog(msg.dialogId);
        return;
      case 'presence':
        this.patchSnapshot({
          participants: msg.participants,
          controllerId: msg.controllerId,
          controller: msg.controller,
        });
        return;
      case 'tabs.changed': {
        this.patchSnapshot({ tabs: msg.tabs, activeTabId: msg.activeTabId });
        const current = this.state.tabId;
        if (current && !msg.tabs.some((t) => t.tabId === current)) {
          this.opts.frames.drop(current);
          const next = msg.activeTabId ?? msg.tabs[0]?.tabId;
          this.setState({ tabId: undefined });
          if (next) void this.selectTab(next).catch(() => {});
        } else if (!current) {
          const next = msg.activeTabId ?? msg.tabs[0]?.tabId;
          if (next) void this.selectTab(next).catch(() => {});
        }
        return;
      }
      case 'browser.state':
        this.patchRuntime(msg.runtime, msg.error);
        return;
      case 'support-session.state': {
        const current = this.state.snapshot?.supportSession;
        if (current?.id === msg.supportSessionId) {
          this.patchSnapshot({ supportSession: { ...current, status: msg.status } });
        }
        return;
      }
      case 'tab.navigated': {
        const snap = this.state.snapshot;
        if (snap) {
          this.patchSnapshot({
            tabs: snap.tabs.map((t) =>
              t.tabId === msg.tabId ? { ...t, url: msg.url, title: msg.title ?? t.title } : t,
            ),
          });
        }
        if (msg.blocked) this.emit({ type: 'navigation_blocked', tabId: msg.tabId, url: msg.url });
        return;
      }
      case 'stream.state':
        this.setState({
          streams: { ...this.state.streams, [msg.tabId]: { state: msg.state, reason: msg.reason } },
        });
        return;
      case 'control.changed': {
        const isMe = msg.controllerId === this.state.participantId;
        const holder = msg.controllerId
          ? this.state.snapshot?.participants.find((p) => p.participantId === msg.controllerId)
          : undefined;
        this.patchSnapshot({
          controllerId: msg.controllerId,
          controller: holder
            ? {
                participantId: holder.participantId,
                name: holder.name,
                kind: holder.kind,
                connected: true,
              }
            : undefined,
        });
        if (this.state.request && !isMe) this.setState({ request: undefined });
        this.emit({
          type: 'control.changed',
          controllerId: msg.controllerId,
          reason: msg.reason,
          isMe,
        });
        return;
      }
      case 'control.requested':
        this.setState({
          request: { participantId: msg.participantId, name: msg.name, kind: msg.kind },
        });
        return;
      case 'control.denied':
        this.emit({ type: 'control.denied' });
        return;
      case 'window.resized':
        this.patchSize(msg.size);
        return;
      case 'filechooser.opened':
        this.setState({
          chooser: { tabId: msg.tabId, chooserId: msg.chooserId, multiple: msg.multiple },
          progress: undefined,
        });
        return;
      case 'filechooser.closed':
        if (this.state.chooser?.chooserId === msg.chooserId) this.clearChooser();
        return;
      case 'file.progress':
        this.setState({ progress: { chooserId: msg.chooserId, receivedBytes: msg.receivedBytes } });
        return;
      case 'notice':
        this.emit({ type: 'notice', level: msg.level, message: msg.message });
        return;
      case 'kicked':
        this.stopped = true;
        this.setState({ connection: 'closed', closed: { code: 'kicked', message: msg.reason } });
        this.emit({ type: 'kicked', reason: msg.reason });
        return;
      case 'pong':
        return;
    }
  }

  private onReply(msg: Reply) {
    const p = this.pending.get(msg.id);
    if (!p) return;
    this.pending.delete(msg.id);
    clearTimeout(p.timer);
    if (msg.ok) p.resolve(msg.data);
    else p.reject(new RequestError(msg.error.code, msg.error.message));
  }

  private patchRuntime(runtime: BrowserRuntime, error?: ErrorInfo) {
    this.patchSnapshot({ runtime });
    this.setState({ runtimeError: error });
  }

  private patchSize(size: WindowSize) {
    this.patchSnapshot({ size });
  }
}
